import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FileText, Eye, Calendar, User, Home, AlertTriangle } from 'lucide-react'
import { useReports } from '@/hooks/use-reports'
import { useNavigate } from 'react-router'
import type { ProjectReport, Category, Tag } from '@tatatemplate/shared-lib'

export default function ReportsPage() {
  const navigate = useNavigate()

  const { data: reportsData, isLoading, error } = useReports({
    sortBy: "reportDate",
    sortOrder: "desc",
    limit: 20
  })

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'MILESTONE':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'FINAL':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'ISSUE':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Enhanced Header Section */}
      <div className="relative bg-gradient-to-r from-blue-600 via-indigo-700 to-purple-800 overflow-hidden">
        <div className="absolute inset-0 bg-black/10">
          <div className="absolute inset-0 bg-white/5"></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-white/5 rounded-full"></div>
        <div className="absolute bottom-0 left-0 -mb-32 -ml-32 w-64 h-64 bg-white/5 rounded-full"></div>

        <div className="relative container mx-auto px-6 py-16">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm border border-white/20">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <div className="flex items-center space-x-2 text-white/80 text-sm">
                <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                <span>Analytics</span>
                <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                <span>Laporan & Analisis</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Laporan & Analisis
            </h1>

            <p className="text-xl text-white/90 mb-8 leading-relaxed max-w-3xl">
              Kumpulan laporan komprehensif, analisis strategis, dan dokumentasi progress proyek untuk pengambilan keputusan yang tepat
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm font-semibold px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                onClick={() => navigate('/')}
              >
                <Home className="h-4 w-4 mr-2" />
                Kembali ke Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-6 py-8 space-y-8">
        {/* Reports Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse border-0 shadow-lg">
                <CardHeader>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-16 w-16 text-orange-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Gagal memuat laporan</h3>
              <p className="text-gray-500">Terjadi kesalahan saat memuat data. Silakan coba lagi nanti.</p>
            </CardContent>
          </Card>
        ) : reportsData?.data && reportsData.data.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reportsData.data.map((report: ProjectReport) => (
              <Card key={report.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/90 backdrop-blur-sm hover:-translate-y-1">
                {report.featuredImage && (
                  <div className="aspect-video bg-gray-100 rounded-t-xl overflow-hidden">
                    <img 
                      src={report.featuredImage} 
                      alt={report.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start gap-2">
                    <CardTitle className="flex items-center gap-2 text-gray-900 font-bold group-hover:text-blue-600 transition-colors">
                      <FileText className="h-5 w-5 text-blue-600" />
                      {report.title}
                    </CardTitle>
                    <Badge className={getTypeColor(report.type)} variant="outline">
                      {report.type === 'PROGRESS' ? 'Progress' :
                       report.type === 'MILESTONE' ? 'Milestone' :
                       report.type === 'FINAL' ? 'Final' : 
                       report.type === 'ISSUE' ? 'Issue' : report.type}
                    </Badge>
                  </div>
                  <CardDescription className="text-gray-600 text-sm line-clamp-2">
                    {report.summary || report.content?.substring(0, 120) + '...'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    {/* Report Meta */}
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-green-500" />
                        <span>{new Date(report.reportDate).toLocaleDateString('id-ID')}</span>
                      </div>
                      {report.author && (
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-purple-500" />
                          <span className="truncate">{report.author.name}</span>
                        </div>
                      )}
                    </div>

                    {/* Project Reference */}
                    {report.project && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Proyek Terkait:</p>
                        <p className="font-medium text-gray-900 text-sm truncate">{report.project.title}</p>
                      </div>
                    )}

                    {/* Categories and Tags */}
                    {report.categories && report.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {report.categories.slice(0, 2).map((category: any) => (
                          <Badge key={category.id} variant="outline" className="text-xs border-gray-300 text-gray-600">
                            {category.name}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Tags */}
                    {report.tags && report.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {report.tags.slice(0, 3).map((tag: any) => (
                          <Badge key={tag.id} variant="secondary" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            #{tag.name}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Eye className="h-3 w-3" />
                        <span>Laporan {report.type.toLowerCase()}</span>
                      </div>
                      <div className="flex justify-end">
                        <Button 
                          size="sm"
                          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0 rounded-lg shadow-md hover:shadow-lg transition-all"
                          onClick={() => navigate(`/reports/${report.slug}`)}
                        >
                          Lihat Detail
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <div className="relative">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                  <FileText className="h-12 w-12 text-blue-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Tidak ada laporan ditemukan</h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Belum ada laporan yang dibuat saat ini.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Load More */}
        {reportsData?.data && reportsData.data.length > 0 && (
          <div className="flex justify-center">
            <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-gray-600 mb-4">Menampilkan {reportsData.data.length} laporan</p>
                  <Button 
                    variant="outline"
                    className="rounded-xl border-gray-300 hover:bg-gray-50 px-8 py-3"
                  >
                    Muat Lebih Banyak Laporan
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
